import { Component, DestroyRef, Input, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { environment } from 'src/environments/environment';
import { LabelLayout, isPortraitLabel, labelFontSizesMm, labelQrSizeMm } from '../../services/label-layout';
import { LabelPrintService } from '../../services/label-print.service';

/** CSS pixels per mm, doubled so the QR code is drawn sharper than it is shown. */
const QR_PX_PER_MM = 96 / 25.4 * 2;

@Component({
    selector: 'app-stock-label',
    templateUrl: './stock-label.component.html',
    styleUrls: ['./stock-label.component.scss'],
    standalone: false
})
export class StockLabelComponent {

  /** 'product' or 'location'. The scanner keys off this segment. */
  @Input()
  scanType: string = '';

  /**
   * Id of the saved row the label points at. Never aisle/shelf text: locations
   * are only unique within a warehouse, and the id is what the scanner looks up.
   */
  @Input()
  recordId: number = -1;

  @Input()
  title: string = '';

  @Input()
  subtitle: string = '';

  @Input()
  reference: string = '';

  layout: LabelLayout;

  private readonly destroyRef = inject(DestroyRef);

  constructor(labelPrint: LabelPrintService) {
    this.layout = labelPrint.layout;

    labelPrint.layout$
    .pipe(takeUntilDestroyed(this.destroyRef))
    .subscribe((layout) => this.layout = layout);
  }

  /** Zebra labels are drawn at the configured stock size; sheet labels keep their fixed size. */
  isZebra(): boolean {
    return this.layout.mode === 'zebra';
  }

  isPortrait(): boolean {
    return this.isZebra() && isPortraitLabel(this.layout);
  }

  qrSizeMm(): number {
    return labelQrSizeMm(this.layout);
  }

  /** Pixel size the QR code is rendered at; 140 is the original sheet label size. */
  qrSizePx(): number {
    return this.isZebra() ? Math.round(this.qrSizeMm() * QR_PX_PER_MM) : 140;
  }

  fontSizesMm(): { title: number, subtitle: number, reference: number } {
    return labelFontSizesMm(this.layout);
  }

  /**
   * Labels are read back as {publicUrl}scan/{type}/{id}. Only the trailing
   * segments are significant to the scanner, so a change of deployment hostname
   * does not invalidate a label already stuck to a shelf.
   */
  getPayload(): string {
    const base = environment.publicUrl.endsWith('/') ? environment.publicUrl : environment.publicUrl + '/';

    return base + 'scan/' + this.scanType + '/' + this.recordId;
  }

  /**
   * A label for an unsaved record would carry an id the scanner rejects, so
   * there is nothing worth printing.
   */
  isPrintable(): boolean {
    return (this.scanType === 'product' || this.scanType === 'location') && this.recordId > 0;
  }
}
